import React, { useState } from "react";
import PropTypes from "prop-types";

// Styles (inline for simplicity, move to a CSS file in production)
const styles = {
  container: {
    padding: "10px",
    fontFamily: "Arial, sans-serif",
    maxWidth: "1200px",
    margin: "0 auto",
  },
  title: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "8px",
    color: "#333",
  },
  radioGroup: {
    marginBottom: "10px",
    display: "flex",
    alignItems: "center",
    gap: "20px",
  },
  radioLabel: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    cursor: "pointer",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "10px",
    fontSize: "12px", // Smaller font size for compact look
  },
  tableHeader: {
    fontWeight: "bold",
    padding: "4px", // Reduced padding
    textAlign: "left",
    backgroundColor: "#e6f7ff", // Light blue header
  },
  tableRow: {
    borderBottom: "1px solid #ddd",
  },
  tableCell: {
    padding: "4px", // Reduced padding
    textAlign: "left",
  },
  rightAlign: {
    textAlign: "right",
  },
  centerAlign: {
    textAlign: "center",
  },
  subTable: {
    margin: "5px 0",
    padding: "5px",
  },
  sprintHeader: {
    backgroundColor: "#e6f7ff", // Light blue header
    padding: "8px", // Reduced padding
    borderRadius: "8px",
    marginBottom: "10px",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    "&:hover": {
      backgroundColor: "#d1e9ff", // Slightly darker blue on hover
    },
  },
  sprintHeaderTitle: {
    fontSize: "14px",
    fontWeight: "bold",
    marginBottom: "5px",
  },
  sprintHeaderDetails: {
    fontSize: "12px",
    color: "#555",
  },
  subtotalRow: {
    backgroundColor: "#f9f9f9",
    fontWeight: "bold",
  },
};

// Helper function to format currency
const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Helper function to format hours
const formatHours = (value) => {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value) + "H";
};

// Helper function to normalize dates
const normalizeDate = (dateString) => {
  const date = new Date(dateString + "T00:00:00"); // Treat as local time
  date.setHours(0, 0, 0, 0); // Strip time components
  return date;
};

// Helper function to format dates
const formatDate = (date) => {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
};

// Reusable RadioGroup Component
const RadioGroup = ({ selectedOption, setSelectedOption, formData }) => {
  const phaseType = formData.implementationType === "Agile" ? "Sprint" : "Milestone";

  return (
    <div style={styles.radioGroup}>
      <label style={styles.radioLabel}>
        <input
          type="radio"
          value="allocation"
          checked={selectedOption === "allocation"}
          onChange={() => setSelectedOption("allocation")}
        />
        Project Cost and Allocation Breakdown by {phaseType}
      </label>
      <label style={styles.radioLabel}>
        <input
          type="radio"
          value="utilization"
          checked={selectedOption === "utilization"}
          onChange={() => setSelectedOption("utilization")}
        />
        Resource Utilization by {phaseType}
      </label>
      <label style={styles.radioLabel}>
        <input
          type="radio"
          value="material"
          checked={selectedOption === "material"}
          onChange={() => setSelectedOption("material")}
        />
        Project Material Cost
      </label>
    </div>
  );
};

// Reusable SprintTable Component
const SprintTable = ({ data, resourceTable, formData }) => {
  const [expandedSprints, setExpandedSprints] = useState({});

  const toggleSprint = (sprintId) => {
    setExpandedSprints((prev) => ({
      ...prev,
      [sprintId]: !prev[sprintId],
    }));
  };

  return (
    <div>
      {data.map((sprintRow, index) => {
        const sprintId = sprintRow.sprint.name;
        const isExpanded = expandedSprints[sprintId];
        const allocatedResources = Object.values(sprintRow.allocations).filter(
          (allocation) => allocation.allocatedHours > 0
        ).length;
        const sprintBandwidth =
          sprintRow.sprint.workingDays *
          formData.businessHoursPerDay *
          allocatedResources;
        const totalAllocatedHours = Object.values(sprintRow.allocations).reduce(
          (sum, allocation) => sum + allocation.allocatedHours,
          0
        );
        const totalCost = Object.entries(sprintRow.allocations).reduce(
          (sum, [resourceId, allocation]) => {
            const resource = resourceTable.find((res) => res.id === resourceId);
            return sum + allocation.allocatedHours * (resource?.reference.hourlyRate || 0);
          },
          0
        );
        const utilizationPercentage = ((totalAllocatedHours / sprintBandwidth) * 100).toFixed(2);

        // Generate sprint alias dynamically
        const prefix = formData.implementationType === "Agile" ? "Sprt" : "Mlst";
        const phaseType = formData.implementationType === "Agile" ? "Sprint" : "Milestone";
        const displayName = `${prefix}${String(index + 1).padStart(2, "0")}`;

        // Normalize dates
        const plannedStart = normalizeDate(sprintRow.sprint.startDate);
        const actualStart = normalizeDate(sprintRow.sprint.actualStartDate);
        const plannedEnd = normalizeDate(sprintRow.sprint.endDate);
        const actualEnd = normalizeDate(sprintRow.sprint.actualEndDate);

        return (
          <div key={index} style={styles.subTable}>
            <div
              style={styles.sprintHeader}
              onClick={() => toggleSprint(sprintId)}
            >
              <div style={styles.sprintHeaderTitle}>
                {displayName}: {sprintRow.sprint.name}
              </div>
              <div style={styles.sprintHeaderDetails}>
              <strong>Working Days:</strong> {sprintRow.sprint.workingDays} |{" "}<strong>Start:</strong> {formatDate(plannedStart)}
                {sprintRow.sprint.startDate !== sprintRow.sprint.actualStartDate && (
                  <> (Actual Start: {formatDate(actualStart)})</>
                )} | <strong>End:</strong> {formatDate(plannedEnd)}
                {sprintRow.sprint.endDate !== sprintRow.sprint.actualEndDate && (
                  <> (Actual End: {formatDate(actualEnd)})</>
                )}
              </div>
              {sprintRow.sprint.holidays?.length > 0 && (
                <div style={styles.sprintHeaderDetails}>
                  <strong>Holidays:</strong> {sprintRow.sprint.holidays.join(", ")}
                </div>
              )}
              <div style={styles.sprintHeaderDetails}>                
                <strong>Resources:</strong> {allocatedResources} |{" "}
                <strong>Allocated Hours:</strong> {formatHours(totalAllocatedHours)} |{" "}
                <strong>Bandwidth:</strong> {formatHours(sprintBandwidth)} |{" "}
                <strong>Utilization Rate:</strong> {utilizationPercentage}% {" "}
              </div>
              <div style={styles.sprintHeaderDetails}>                
                <strong>Total Cost:</strong> {formatCurrency(totalCost)}
              </div>
             
            </div>
            {isExpanded && (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.tableHeader, width: "50%" }}>Resource</th>
                    <th style={{ ...styles.tableHeader, width: "25%", ...styles.rightAlign }}>Allocated Hours</th>
                    <th style={{ ...styles.tableHeader, width: "25%", ...styles.rightAlign }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(sprintRow.allocations)
                    .filter(([_, allocation]) => allocation.allocatedHours > 0)
                    .map(([resourceId, allocation]) => {
                      const resource = resourceTable.find((res) => res.id === resourceId);
                      const cost = (allocation.allocatedHours * (resource?.reference.hourlyRate || 0)).toFixed(2);

                      return (
                        <tr key={resourceId} style={styles.tableRow}>
                          <td style={styles.tableCell}>{resource?.role || "N/A"}</td>
                          <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatHours(allocation.allocatedHours)}</td>
                          <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatCurrency(cost)}</td>
                        </tr>
                      );
                    })}
                  <tr style={styles.subtotalRow}>
                    <td style={styles.tableCell}>Subtotal</td>
                    <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatHours(totalAllocatedHours)}</td>
                    <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatCurrency(totalCost)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Reusable ResourceTable Component
const ResourceTable = ({ data, resourceAllocationGrid, formData }) => {
  const [expandedResources, setExpandedResources] = useState({});

  const toggleResource = (resourceId) => {
    setExpandedResources((prev) => ({
      ...prev,
      [resourceId]: !prev[resourceId],
    }));
  };

  return (
    <div>
      {data.map((resource, index) => {
        const resourceId = resource.id;
        const isExpanded = expandedResources[resourceId];
        const allocatedSprints = resourceAllocationGrid.filter(
          (sprintRow) => sprintRow.allocations[resource.id]?.allocatedHours > 0
        );
        const resourceBandwidth =
          allocatedSprints.reduce(
            (sum, sprintRow) => sum + sprintRow.sprint.workingDays,
            0
          ) * formData.businessHoursPerDay;
        const totalAllocatedHours = allocatedSprints.reduce(
          (sum, sprintRow) => sum + sprintRow.allocations[resource.id]?.allocatedHours,
          0
        );
        const totalCost = allocatedSprints.reduce(
          (sum, sprintRow) => {
            const allocation = sprintRow.allocations[resource.id];
            return sum + allocation.allocatedHours * (resource?.reference.hourlyRate || 0);
          },
          0
        );
        const utilizationPercentage = ((totalAllocatedHours / resourceBandwidth) * 100).toFixed(2);

        return (
          <div key={index} style={styles.subTable}>
            <div
              style={styles.sprintHeader}
              onClick={() => toggleResource(resourceId)}
            >
              <div style={styles.sprintHeaderTitle}>
                {resource.role} ({resource.alias})
              </div>
              <div style={styles.sprintHeaderDetails}>
                <strong>Allocated:</strong> {formatHours(totalAllocatedHours)} |{" "}
                <strong>Bandwidth:</strong> {formatHours(resourceBandwidth)} |{" "}
                <strong>Utilization:</strong> {utilizationPercentage}%
              </div>
            </div>
            {isExpanded && (
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={{ ...styles.tableHeader, width: "50%" }}>Sprint</th>
                    <th style={{ ...styles.tableHeader, width: "25%", ...styles.rightAlign }}>Allocated Hours</th>
                    <th style={{ ...styles.tableHeader, width: "25%", ...styles.rightAlign }}>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {allocatedSprints.map((sprintRow) => {
                    const allocation = sprintRow.allocations[resource.id];
                    const cost = (allocation.allocatedHours * (resource?.reference.hourlyRate || 0)).toFixed(2);

                    return (
                      <tr key={sprintRow.sprint.name} style={styles.tableRow}>
                        <td style={styles.tableCell}>{sprintRow.sprint.name}</td>
                        <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatHours(allocation.allocatedHours)}</td>
                        <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatCurrency(cost)}</td>
                      </tr>
                    );
                  })}
                  <tr style={styles.subtotalRow}>
                    <td style={styles.tableCell}>Subtotal</td>
                    <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatHours(totalAllocatedHours)}</td>
                    <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatCurrency(totalCost)}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        );
      })}
    </div>
  );
};

// Project Material Cost Table
const ProjectMaterialCostTable = ({ projectExpenses }) => {
  if (!projectExpenses || projectExpenses.length === 0) {
    return <p>No project expenses found.</p>;
  }

  // Calculate the total cost of all project expenses
  const totalCost = projectExpenses.reduce(
    (sum, expense) => sum + expense.quantity * expense.unitPrice,
    0
  );

  return (
    <table style={styles.table}>
      <thead>
        <tr>
          <th style={{ ...styles.tableHeader, width: "16%" }}>Item</th>
          <th style={{ ...styles.tableHeader, width: "16%" }}>Manufacturer</th>
          <th style={{ ...styles.tableHeader, width: "23%" }}>Description</th>
          <th style={{ ...styles.tableHeader, width: "15%", ...styles.rightAlign }}>Quantity</th>
          <th style={{ ...styles.tableHeader, width: "15%", ...styles.rightAlign }}>Unit Cost</th>
          <th style={{ ...styles.tableHeader, width: "15%", ...styles.rightAlign }}>Total Cost</th>
        </tr>
      </thead>
      <tbody>
        {projectExpenses.map((expense, index) => (
          <tr key={index} style={styles.tableRow}>
            <td style={styles.tableCell}>{expense.item}</td>
            <td style={styles.tableCell}>{expense.manufacturer}</td>
            <td style={styles.tableCell}>{expense.description}</td>
            <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{expense.quantity}</td>
            <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatCurrency(expense.unitPrice)}</td>
            <td style={{ ...styles.tableCell, ...styles.rightAlign }}>{formatCurrency(expense.quantity * expense.unitPrice)}</td>
          </tr>
        ))}
        {/* Subtotal Row */}
        <tr style={styles.subtotalRow}>
          <td colSpan={5} style={{ ...styles.tableCell, textAlign: "right", fontWeight: "bold" }}>Subtotal</td>
          <td style={{ ...styles.tableCell, ...styles.rightAlign, fontWeight: "bold" }}>{formatCurrency(totalCost)}</td>
        </tr>
      </tbody>
    </table>
  );
};

// Main ResourceAllocationReport Component
const ResourceAllocationReport = ({ formData }) => {
  const [selectedOption, setSelectedOption] = useState("allocation");

  if (!formData || !formData.resourceAllocationGrid) {
    return <p>Click on 'Generate Estimate' to load resource allocation and budget breakdown data...</p>;
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>📊 Resource Allocation and Estimated Cost Breakdown</h3>

      <RadioGroup
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        formData={formData}
      />

      {selectedOption === "allocation" ? (
        <SprintTable
          data={formData.resourceAllocationGrid}
          resourceTable={formData.resourceTable}
          formData={formData}
        />
      ) : selectedOption === "utilization" ? (
        <ResourceTable
          data={formData.resourceTable}
          resourceAllocationGrid={formData.resourceAllocationGrid}
          formData={formData}
        />
      ) : (
        <ProjectMaterialCostTable projectExpenses={formData.projectExpenses} />
      )}
    </div>
  );
};

ResourceAllocationReport.propTypes = {
  formData: PropTypes.shape({
    resourceAllocationGrid: PropTypes.array.isRequired,
    sprintsMilestones: PropTypes.array.isRequired,
    businessHoursPerDay: PropTypes.number.isRequired,
    resourceTable: PropTypes.array.isRequired,
    implementationType: PropTypes.string.isRequired,
    projectExpenses: PropTypes.array.isRequired,
  }).isRequired,
};

export default ResourceAllocationReport;